import { Link } from "react-router-dom";
import FaqAccordion from "../../components/shared/FaqAccordion.jsx";

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontSize: "22px",
  color: "var(--color-ink)",
  margin: 0,
};
const roleTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontSize: "18px",
  color: "var(--color-ink)",
  margin: 0,
};

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

function Role({ title, body }) {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "24px 26px" }}>
      <p style={roleTitleStyle}>{title}</p>
      <p style={bodyStyle}>{body}</p>
    </div>
  );
}

/** Shared with SupportSubpage.jsx for the FAQPage JSON-LD — keep in sync
 * with the rendered <Role>/FAQ text below. */
export const VOLUNTEER_ROLES = {
  de: [
    {
      title: "Orga & Eventplanung",
      body: "Orte anfragen, Anmeldungen verwalten, Ablaufpläne schreiben, am Event-Tag den Überblick behalten. Für Menschen, die gern Listen führen und Dinge zum Laufen bringen. Meist von zuhause, dazu 1–2 Treffen pro Event.",
    },
    {
      title: "Teamer für Jugend-LARP",
      body: "Jugendliche bei Tagesevents und Ferienangeboten begleiten: Spiel anleiten, Sicherheit im Blick behalten, ansprechbar sein. Wir schulen dich, du brauchst ein erweitertes Führungszeugnis. Pädagogische Vorerfahrung hilft, ist aber keine Bedingung.",
    },
    {
      title: "Kulissen-, Requisiten- und Gewandungsbau",
      body: "Tavernenschilder, Schatztruhen, Banner, Leihgewandung. Wer mit Holz, Stoff oder Farbe umgehen kann, ist hier richtig. Bautage am Wochenende.",
    },
    {
      title: "NSC spielen",
      body: "Für ein paar Stunden oder ein Wochenende eine Rolle aus unserer Geschichte übernehmen: Händlerin, Bote, Räuber, Wirt. Gewandung stellen wir. Der leichteste Einstieg ins LARP — und der, bei dem du am meisten Dank bekommst.",
    },
    {
      title: "Foto, Video, Social Media",
      body: "Events dokumentieren, Reels schneiden, den Newsletter mitschreiben. Wenn du eine Kamera hast oder Instagram verstehst, brauchen wir dich.",
    },
  ],
  en: [
    {
      title: "Organising & event planning",
      body: "Requesting venues, managing registrations, writing running orders, keeping an overview on event day. For people who like keeping lists and making things happen. Mostly remote, plus 1–2 meetings per event.",
    },
    {
      title: "Youth LARP supervisor",
      body: "Support young people at day events and holiday programmes: guide play, keep an eye on safety, be approachable. We train you; you'll need an extended background check. Prior pedagogical experience helps but isn't required.",
    },
    {
      title: "Set, props & costume building",
      body: "Tavern signs, treasure chests, banners, loaner costumes. If you're handy with wood, fabric, or paint, this is for you. Build days on weekends.",
    },
    {
      title: "Playing NPCs",
      body: "Take on a role from our story for a few hours or a whole weekend: merchant, messenger, bandit, innkeeper. We provide the costume. The easiest way into LARP — and the one that gets you the most thanks.",
    },
    {
      title: "Photo, video, social media",
      body: "Documenting events, editing reels, helping write the newsletter. If you have a camera or understand Instagram, we need you.",
    },
  ],
};

export const VOLUNTEER_FAQ = {
  de: [
    { question: "Muss ich LARP-Erfahrung haben?", answer: "Nein. Die meisten unserer Ehrenamtlichen hatten vor ihrem ersten Einsatz noch nie gespielt." },
    { question: "Bekomme ich eine Ehrenamtsbescheinigung?", answer: "Ja, auf Wunsch — auch für die Ehrenamtskarte Sachsen." },
    { question: "Kann ich auch aus Leipzig oder Chemnitz mitmachen?", answer: "Ja, Orga und Social Media gehen von überall; Bau- und Event-Tage sind immer dort, wo wir gerade unsere Events veranstalten." },
    {
      question: "Kann man auf Events normal mitspielen?",
      answer: "Kommt auf die Funktion an. Viele Aufgaben — Auf- und Abbau, Schichten in der Taverne und Ähnliches — erledigst du vor dem Event oder in festen Schichten während des Events, sodass genug Zeit für dein eigenes Spiel bleibt. Bei anderen Rollen, etwa als Spielleitung, kann dich die Aufgabe je nach Event auch vollständig einnehmen. Wir sprechen das vorher mit dir ab, damit du weißt, worauf du dich einlässt.",
    },
  ],
  en: [
    { question: "Do I need LARP experience?", answer: "No. Most of our volunteers had never played before their first shift." },
    { question: "Do I get a volunteering certificate?", answer: "Yes, on request — including for the Saxony volunteer card (Ehrenamtskarte)." },
    { question: "Can I join from Leipzig or Chemnitz?", answer: "Yes — organising and social media work from anywhere; build and event days are always wherever we're currently holding our events." },
    {
      question: "Can I play normally at events too?",
      answer: "Depends on the role. Many tasks — set-up and pack-down, tavern shifts, and similar — happen before the event or in fixed shifts during it, so you still have enough time for your own play. Other roles, like being a game master, can occupy you fully for the whole event, depending on what's needed. We talk this through with you beforehand so you know what you're signing up for.",
    },
  ],
};

/**
 * "Ehrenamt" copy. The original association-provided intro paragraphs
 * (still true and still used below) are kept; the role/FAQ breakdown adds
 * the structure the SEO brief asked for.
 */
export function VolunteerDe({ jugendschutzPath, verhaltenskodexPath, larpRollenPath }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <p style={bodyStyle}>
          Tales&amp;Legends lebt von Menschen, die Lust haben, Welten zu bauen — im Wortsinn und im übertragenen.
          Wir sind eine gemeinnützige Organisation aus Dresden und suchen Ehrenamtliche für unsere LARP-Events, für
          die Jugendarbeit und für alles dazwischen. Du musst nicht LARPen können. Du musst nicht mal wissen, was
          das ist. Du brauchst nur ein paar Stunden im Monat und die Bereitschaft, Teil eines Teams zu sein.
        </p>
        <p style={bodyStyle}>
          Als kleines Dankeschön ermöglichen wir engagierten Ehrenamtlichen je nach Umfang des Einsatzes vergünstigte
          oder kostenfreie Tickets für unsere Events.
        </p>
      </div>

      <Section title="Was du bei uns tun kannst">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "18px" }}>
          {VOLUNTEER_ROLES.de.map((role) => (
            <Role key={role.title} {...role} />
          ))}
        </div>
      </Section>

      <Section title="Wie viel Zeit das braucht">
        <p style={bodyStyle}>
          Ehrlich: so viel, wie du geben willst. Ein NSC-Einsatz ist ein Abend. Orga-Arbeit sind 2–4 Stunden im
          Monat plus die Event-Tage. Teamer für Jugend-LARP planen wir pro Angebot ein, meist ein Wochenende im
          Quartal. Wir arbeiten mit festen Ansprechpersonen und klaren Aufgaben — niemand muss alles machen, und
          niemand steht allein da.
        </p>
      </Section>

      <Section title="Voraussetzungen">
        <p style={bodyStyle}>
          Mindestalter 16 (Teamer für Jugend-LARP: 18). Für die Arbeit mit Jugendlichen ein erweitertes
          Führungszeugnis und die Unterschrift unter unsere Selbstverpflichtung — beides Teil unseres{" "}
          {jugendschutzPath ? <Link to={jugendschutzPath}>Jugendschutzkonzepts</Link> : "Jugendschutzkonzepts"}. Sonst:
          Zuverlässigkeit und Respekt. Unser{" "}
          {verhaltenskodexPath ? <Link to={verhaltenskodexPath}>Verhaltenskodex</Link> : "Verhaltenskodex"} gilt für
          alle im Team.
        </p>
      </Section>

      <Section title="So startest du">
        <ol style={{ display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" }}>
          <li style={bodyStyle}>Formular ausfüllen (Name, Mail, Interesse, Zeit)</li>
          <li style={bodyStyle}>Kennenlerngespräch (30 Minuten, online oder in Dresden)</li>
          <li style={bodyStyle}>Erster Einsatz mit einer festen Ansprechperson</li>
        </ol>
      </Section>

      <Section title="Häufige Fragen">
        <FaqAccordion items={VOLUNTEER_FAQ.de} />
      </Section>

      <p style={bodyStyle}>
        Du hast Interesse oder möchtest einfach mal unverbindlich reinschnuppern? Schreib uns — wir freuen uns über
        jede:n, der die Welt von Tales&amp;Legends ein Stück größer macht.
      </p>
    </div>
  );
}

/** Courtesy translation — German is the primary wording (see VolunteerDe). */
export function VolunteerEn({ jugendschutzPath, verhaltenskodexPath }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "36px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <p style={bodyStyle}>
          Tales&amp;Legends lives on people who want to build worlds — literally and figuratively. We're a non-profit
          from Dresden looking for volunteers for our LARP events, our youth work, and everything in between. You
          don't need to know how to LARP. You don't even need to know what it is. You just need a few hours a
          month and the willingness to be part of a team.
        </p>
        <p style={bodyStyle}>
          As a small thank-you, depending on the scope of your involvement, we offer engaged volunteers discounted
          or free tickets to our events.
        </p>
      </div>

      <Section title="What you can do with us">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "18px" }}>
          {VOLUNTEER_ROLES.en.map((role) => (
            <Role key={role.title} {...role} />
          ))}
        </div>
      </Section>

      <Section title="How much time it takes">
        <p style={bodyStyle}>
          Honestly: as much as you want to give. An NPC shift is one evening. Organising work is 2–4 hours a month
          plus event days. We plan youth LARP supervisors per programme, usually one weekend a quarter. We work
          with fixed points of contact and clear tasks — nobody has to do everything, and nobody's on their own.
        </p>
      </Section>

      <Section title="Requirements">
        <p style={bodyStyle}>
          Minimum age 16 (youth LARP supervisors: 18). For work with young people, an extended background
          check and signing our self-commitment declaration — both part of our{" "}
          {jugendschutzPath ? <Link to={jugendschutzPath}>youth protection concept</Link> : "youth protection concept"}.
          Otherwise: reliability and respect. Our{" "}
          {verhaltenskodexPath ? <Link to={verhaltenskodexPath}>code of conduct</Link> : "code of conduct"} applies to
          everyone on the team.
        </p>
      </Section>

      <Section title="How to start">
        <ol style={{ display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" }}>
          <li style={bodyStyle}>Fill in the form (name, email, interest, availability)</li>
          <li style={bodyStyle}>A get-to-know-you call (30 minutes, online or in Dresden)</li>
          <li style={bodyStyle}>Your first shift with a fixed point of contact</li>
        </ol>
      </Section>

      <Section title="Frequently asked questions">
        <FaqAccordion items={VOLUNTEER_FAQ.en} />
      </Section>

      <p style={bodyStyle}>
        Interested, or would you just like to take a no-strings-attached look? Write to us – we're happy about
        everyone who helps make the world of Tales&amp;Legends a little bigger.
      </p>
    </div>
  );
}
